import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { AREAS } from "../data/content";
import { Reveal, Eyebrow } from "./Reveal";

const slug = (t) => t.toLowerCase().replace(/[^a-z]+/g, "-");

const MapPanel = ({ active, setActive }) => (
  <div data-testid="coverage-map" className="relative aspect-[4/3] w-full rounded-[6px] bg-[#121316] border border-white/10 overflow-hidden">
    <div className="absolute inset-0 tactical-grid" />
    <svg viewBox="0 0 100 75" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden="true">
      <path d="M0 14 C 20 18, 35 22, 48 26 S 70 24, 100 16" stroke="#3B82F6" strokeOpacity="0.35" strokeWidth="1.6" fill="none" />
      <path d="M48 26 C 47 40, 46 55, 48 75" stroke="#3B82F6" strokeOpacity="0.25" strokeWidth="0.8" fill="none" />
    </svg>
    {AREAS.map((a) => {
      const on = a.name === active.name;
      return (
        <button
          key={a.name}
          onClick={() => setActive(a)}
          data-testid={`map-pin-${slug(a.name)}`}
          aria-label={a.name}
          style={{ left: `${a.x}%`, top: `${a.y}%` }}
          className="absolute -translate-x-1/2 -translate-y-1/2 group"
        >
          {on && <motion.span layoutId="pin-ring" className="absolute -inset-3 rounded-full border border-[#FF6B00]" />}
          <span className={`block rounded-full transition-[background-color,transform] duration-300 ${on ? "h-4 w-4 bg-[#FF6B00] scale-110" : "h-3 w-3 bg-white/70 group-hover:bg-[#FF6B00]"} ${a.hq ? "ring-4 ring-[#FF6B00]/30" : ""}`} />
          <span className={`absolute left-1/2 -translate-x-1/2 top-5 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.1em] ${on ? "text-white" : "text-zinc-400"} hidden sm:block`}>{a.name}</span>
        </button>
      );
    })}
    <span className="absolute top-4 right-4 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">Ottawa · ON</span>
  </div>
);

export const Coverage = () => {
  const [active, setActive] = useState(AREAS.find((a) => a.hq));
  return (
    <section id="coverage" data-testid="coverage-section" className="relative bg-[#0B0C10] py-24 sm:py-32">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Coverage Area</Eyebrow>
            <h2 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[0.95] text-white">
              Dedicated <br />Ottawa response.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap gap-2.5" data-testid="coverage-tags">
              {AREAS.map((a) => {
                const on = a.name === active.name;
                return (
                  <button
                    key={a.name}
                    onClick={() => setActive(a)}
                    onMouseEnter={() => setActive(a)}
                    data-testid={`area-tag-${slug(a.name)}`}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-[background-color,color,border-color,transform] duration-300 hover:-translate-y-0.5 ${on ? "bg-[#FF6B00] border-[#FF6B00] text-[#0B0C10]" : "border-white/15 text-zinc-300 hover:border-[#FF6B00] hover:text-white"}`}
                  >
                    {a.name}
                  </button>
                );
              })}
            </div>
            <motion.div key={active.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="coverage-active" className="mt-8 flex items-start gap-4 rounded-[6px] bg-white text-[#0B0C10] p-5">
              <MapPin className="h-5 w-5 text-[#FF6B00] mt-0.5" />
              <div>
                <p className="font-display font-bold text-xl">{active.name}</p>
                <p className="font-mono text-xs uppercase tracking-[0.14em] text-zinc-600 mt-1">{active.region}</p>
                <p className="text-sm text-zinc-700 mt-2">Inspections, work-order response, repairs and emergency dispatch available.</p>
              </div>
            </motion.div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-7">
          <MapPanel active={active} setActive={setActive} />
        </Reveal>
      </div>
    </section>
  );
};
